import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Mail, Send, Loader2 } from "lucide-react";
import { Reveal, Eyebrow, SectionTitle } from "@/components/Reveal";
import { api, errMsg, EMAIL } from "@/lib/api";

const EMPTY = { name: "", email: "", service: "pictures", details: "", budget: "" };
const OPTS = [["pictures", "Pictures — $20"], ["videos", "Videos — $40"], ["websites", "Website — $40"], ["other", "Something else"]];

export const Contact = () => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const { user } = useAuth();
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    if (user) setF((p) => ({ ...p, name: p.name || user.name || "", email: p.email || user.email || "" }));
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", { ...f, budget: f.budget || null });
      setF({ ...EMPTY, name: user?.name || "", email: user?.email || "" });
      setDone(true);
      toast.success("Request sent. I'll get back to you within 1–2 business days.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 sm:py-40 bg-[var(--crypt)]/50 border-t border-[var(--line)]">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-10 grid lg:grid-cols-12 gap-14">
        <Reveal className="lg:col-span-5">
          <Eyebrow n="05">Contact</Eyebrow>
          <SectionTitle>Summon the <span className="italic text-[var(--blood)] glow-blood">King</span>.</SectionTitle>
          <p className="mt-6 text-[var(--ink-dim)] text-base sm:text-lg leading-relaxed">
            Tell me what you need. I get back to customers within 1 to 2 business days.
          </p>
          <a href={`mailto:${EMAIL}`} data-testid="contact-email-link"
            className="mt-10 inline-flex items-center gap-3 font-display text-xl sm:text-3xl break-all text-gilded hover:opacity-80 transition-opacity">
            <Mail className="w-6 h-6 text-[var(--gold)] shrink-0" /> {EMAIL}
          </a>
        </Reveal>
        <Reveal delay={0.12} className="lg:col-span-7">
          <form onSubmit={submit} data-testid="contact-form" className="grid sm:grid-cols-2 gap-4">
            <input required data-testid="contact-name-input" className="field" placeholder="Your name" value={f.name} onChange={up("name")} />
            <input required type="email" data-testid="contact-email-input" className="field" placeholder="Your email" value={f.email} onChange={up("email")} />
            <select data-testid="contact-service-select" className="field" value={f.service} onChange={up("service")}>
              {OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <input data-testid="contact-budget-input" className="field" placeholder="Budget (optional)" value={f.budget} onChange={up("budget")} />
            <textarea required rows={6} data-testid="contact-details-input" className="field sm:col-span-2 resize-none" placeholder="Describe your project — style, size, deadline, links to references…" value={f.details} onChange={up("details")} />
            <button disabled={busy} data-testid="contact-submit-btn" className="btn-blood sm:col-span-2 py-5 font-mono text-sm tracking-[0.25em] uppercase flex items-center justify-center gap-3 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send request
            </button>
            <p data-testid="contact-account-note" className="sm:col-span-2 text-sm text-[var(--ink-dim)]">
              {user?.role === "customer"
                ? <>This request will be saved to <Link to="/account" className="text-[var(--gold)] underline underline-offset-4">your account</Link> so you can track it.</>
                : <><Link to="/account" data-testid="contact-create-profile-link" className="text-[var(--gold)] underline underline-offset-4">Create a free profile</Link> to track your requests.</>}
            </p>
            {done && <p data-testid="contact-success-msg" className="sm:col-span-2 text-sm text-[var(--gold-soft)]">Received. Expect a reply within 1–2 business days.</p>}
          </form>
        </Reveal>
      </div>
    </section>
  );
};
